import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: block;
    }

    :host([fluid]) {
      width: 100%;
    }

    /* ── Outer wrapper ─────────────────────────────────────────────────────── */

    .uwc-editor {
      border:        1px solid var(--uwc-editor-border, ${t.borderControl});
      border-radius: var(--uwc-editor-radius, ${t.radiusMd});
      background:    var(--uwc-editor-bg, ${t.surface});
      font-family:   var(--uwc-editor-font-family, inherit);
      overflow:      hidden;
      transition:    border-color ${t.durationBase}, box-shadow ${t.durationBase};
    }

    .uwc-editor--invalid {
      border-color: ${t.danger};
    }

    :host(:focus-within) .uwc-editor:not(.uwc-editor--invalid) {
      border-color: var(--uwc-editor-focus-border, ${t.primary});
      box-shadow:   0 0 0 3px color-mix(in oklab, ${t.primary} 20%, transparent);
    }

    :host([invalid]:focus-within) .uwc-editor {
      box-shadow: 0 0 0 3px color-mix(in oklab, ${t.danger} 20%, transparent);
    }

    :host([disabled]) .uwc-editor {
      opacity:        0.5;
      pointer-events: none;
    }

    /* ── Toolbar ───────────────────────────────────────────────────────────── */

    .uwc-editor__toolbar {
      display:       flex;
      flex-wrap:     wrap;
      align-items:   center;
      gap:           2px;
      padding:       var(--uwc-editor-toolbar-padding, ${t.space2} ${t.space3});
      background:    var(--uwc-editor-toolbar-bg, ${t.surface});
      border-bottom: 1px solid var(--uwc-editor-toolbar-border, ${t.borderControl});
    }

    .uwc-editor__toolbar-group {
      display:     flex;
      align-items: center;
      gap:         1px;
    }

    .uwc-editor__sep {
      display:     inline-block;
      width:       1px;
      height:      18px;
      background:  ${t.borderControl};
      margin:      0 3px;
      flex-shrink: 0;
    }

    /* ── Toolbar buttons ───────────────────────────────────────────────────── */

    .uwc-editor__btn {
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      min-width:       28px;
      height:          28px;
      padding:         0 5px;
      border:          none;
      border-radius:   ${t.radiusSm};
      background:      transparent;
      color:           var(--uwc-editor-toolbar-color, ${t.textSecondary});
      cursor:          pointer;
      transition:      background ${t.durationFast}, color ${t.durationFast};
      line-height:     1;
    }

    .uwc-editor__btn-label {
      font-size:   12px;
      font-weight: ${t.fontWeightSemibold};
      line-height: 1;
      font-family: inherit;
    }

    /* Typographic styling for inline-format labels */
    .uwc-editor__btn--bold   .uwc-editor__btn-label { font-weight: 800; }
    .uwc-editor__btn--italic .uwc-editor__btn-label { font-style: italic; }
    .uwc-editor__btn--underline .uwc-editor__btn-label { text-decoration: underline; }
    .uwc-editor__btn--strike    .uwc-editor__btn-label { text-decoration: line-through; }

    .uwc-editor__btn:hover {
      background: ${t.hoverBg};
      color:      ${t.text};
    }

    .uwc-editor__btn.is-active {
      background: color-mix(in oklab, ${t.primary} 12%, transparent);
      color:      ${t.primary};
    }

    .uwc-editor__btn:focus-visible {
      outline:        2px solid ${t.primary};
      outline-offset: 1px;
    }

    /* ── Aux bar (link / image inputs) ────────────────────────────────────── */

    .uwc-editor__aux-bar {
      display:       flex;
      align-items:   center;
      gap:           6px;
      padding:       0.3rem 0.6rem;
      background:    ${t.surfaceRaised};
      border-bottom: 1px solid ${t.borderControl};
    }

    .uwc-editor__aux-input {
      flex:          1;
      height:        28px;
      padding:       0 0.5rem;
      border:        1px solid ${t.borderControl};
      border-radius: ${t.radiusSm};
      font-size:     0.8rem;
      font-family:   inherit;
      background:    ${t.surface};
      color:         ${t.text};
      outline:       none;
      min-width:     0;
    }

    .uwc-editor__aux-input--alt {
      flex: 0 0 140px;
    }

    .uwc-editor__aux-input:focus {
      border-color: ${t.primary};
      box-shadow:   0 0 0 2px color-mix(in oklab, ${t.primary} 15%, transparent);
    }

    .uwc-editor__aux-action {
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      height:          28px;
      padding:         0 0.65rem;
      border:          1px solid ${t.borderControl};
      border-radius:   ${t.radiusSm};
      background:      ${t.surface};
      color:           ${t.text};
      font-size:       0.75rem;
      font-family:     inherit;
      font-weight:     ${t.fontWeightMedium};
      cursor:          pointer;
      white-space:     nowrap;
      transition:      background ${t.durationFast};
    }

    .uwc-editor__aux-action:hover {
      background: ${t.hoverBg};
    }

    .uwc-editor__aux-close {
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      width:           28px;
      height:          28px;
      border:          none;
      border-radius:   ${t.radiusSm};
      background:      transparent;
      color:           ${t.textSecondary};
      cursor:          pointer;
      padding:         0;
      transition:      background ${t.durationFast};
    }

    .uwc-editor__aux-close:hover {
      background: ${t.hoverBg};
      color:      ${t.text};
    }

    /* ── Content area ──────────────────────────────────────────────────────── */

    .uwc-editor__content {
      min-height:  var(--uwc-editor-min-height, 200px);
      max-height:  var(--uwc-editor-max-height, none);
      padding:     var(--uwc-editor-content-padding, 0.875rem ${t.space4});
      color:       var(--uwc-editor-color, ${t.text});
      font-size:   var(--uwc-editor-font-size, ${t.fontSizeMd});
      line-height: 1.6;
      overflow-y:  auto;
      outline:     none;
      word-break:  break-word;
    }

    /* Placeholder — shown on empty editor and on empty first paragraph */
    .uwc-editor__content:empty::before,
    .uwc-editor__content > p:only-child:empty::before {
      content:        attr(data-placeholder);
      color:          ${t.textMuted};
      pointer-events: none;
    }

    /* Scrollbar */
    .uwc-editor__content::-webkit-scrollbar       { width: 6px; }
    .uwc-editor__content::-webkit-scrollbar-thumb {
      background:    ${t.borderControl};
      border-radius: ${t.radiusFull};
    }

    /* ── Content typography ────────────────────────────────────────────────── */

    .uwc-editor__content h1 { font-size: 1.75em;  font-weight: ${t.fontWeightBold};     margin: 0.5em 0; line-height: 1.2; }
    .uwc-editor__content h2 { font-size: 1.4em;   font-weight: ${t.fontWeightSemibold}; margin: 0.5em 0; line-height: 1.3; }
    .uwc-editor__content h3 { font-size: 1.15em;  font-weight: ${t.fontWeightSemibold}; margin: 0.5em 0; line-height: 1.4; }
    .uwc-editor__content p  { margin: 0.2em 0; }

    .uwc-editor__content ul,
    .uwc-editor__content ol { padding-left: 1.5em; margin: 0.25em 0; }
    .uwc-editor__content li { margin: 0.15em 0; }

    .uwc-editor__content blockquote {
      border-left: 3px solid ${t.primary};
      margin:      0.5em 0;
      padding:     0.25em 0 0.25em 1em;
      color:       ${t.textSecondary};
      font-style:  italic;
    }

    .uwc-editor__content pre {
      background:    ${t.surfaceRaised};
      border-radius: ${t.radiusSm};
      padding:       0.75em 1em;
      overflow-x:    auto;
      font-family:   ui-monospace, 'Cascadia Code', monospace;
      font-size:     0.875em;
      border:        1px solid ${t.borderControl};
    }

    .uwc-editor__content code {
      background:    ${t.surfaceRaised};
      border-radius: ${t.radiusSm};
      padding:       0.1em 0.3em;
      font-family:   ui-monospace, 'Cascadia Code', monospace;
      font-size:     0.875em;
      border:        1px solid ${t.borderControl};
    }

    .uwc-editor__content pre code {
      background: none;
      padding:    0;
      font-size:  inherit;
      border:     none;
    }

    .uwc-editor__content a {
      color:           ${t.primary};
      text-decoration: underline;
    }

    .uwc-editor__content a:hover {
      opacity: 0.8;
    }

    .uwc-editor__content img {
      max-width:     100%;
      height:        auto;
      border-radius: ${t.radiusSm};
    }

    .uwc-editor__content hr {
      border:     none;
      border-top: 1px solid ${t.borderControl};
      margin:     1em 0;
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-editor         { transition: none; }
      .uwc-editor__btn    { transition: none; }
      .uwc-editor__aux-action { transition: none; }
    }
  `,
];
